import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { LocalStorageStrategy } from './strategies/local-storage.strategy';

/**
 * Factory service for creating isolated StorageService instances.
 *
 * This factory allows components to create multiple independent storage instances,
 * with a localStorage strategy.
 *
 * Unlike providing StorageService directly, this factory ensures each component
 * gets its own isolated instances that don't interfere with each other.
 *
 * @example
 * export class MyComponent {
 *   private localStorage: StorageService;
 *
 *   constructor(private factory: StorageServiceFactory) {
 *     this.localStorage = factory.createLocalStorage();
 *   }
 *
 *   save() {
 *     this.localStorage.set('permanent-key', 'value');
 *   }
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class StorageServiceFactory {
  constructor(private localStorageStrategy: LocalStorageStrategy) {}

  /**
   * Creates a new StorageService instance using localStorage.
   * Data persists across browser sessions.
   *
   * @returns A new isolated StorageService instance
   */
  createLocalStorage(): StorageService {
    return new StorageService(this.localStorageStrategy);
  }
}
